import React, { useState, useEffect, useRef } from 'react'
import { DragDropContext } from 'react-beautiful-dnd'
import { Flipper, Flipped } from 'react-flip-toolkit'

import { useStyles } from './toc-list.style'

import { TextInput } from '../common/components/text-input'
import { Button } from '../common/components/button'
import NotificationManager from '../common/components/Notifications/NotificationManager'

import { CategoryBar } from './category-bar'
import { CategoryPages } from './category-pages'
import { useReportTocContext } from './contexts/report-toc.context'

export function TocList ({ report: reportParam, filteredToc, isEditable, style, className, uncategorizedGroup }) {
  const classes = useStyles()
  const reportTocContext = useReportTocContext() || {}
  const report = isEditable ? reportTocContext.report : reportParam

  const pageNumber = useRef()
  const [groups, setGroups] = useState(filteredToc || report.groups)

  const [newCategory, setNewCategory] = useState('')

  function handleDragEnd (draggable) {
    const { source, destination } = draggable

    if (!destination) {
      return
    }

    const copiedGroups = JSON.parse(JSON.stringify(groups))
    const sourceGroup = copiedGroups.find((group) => (group.id === source.droppableId))
    const destinationGroup = copiedGroups.find((group) => (group.id === destination.droppableId))

    const [removedItem] = sourceGroup.pages.splice(source.index, 1)
    destinationGroup.pages.splice(destination.index, 0, removedItem)

    updateGroups(copiedGroups)
  }

  function handleCategoryCreate (event) {
    event.preventDefault()

    const newGroupId = newCategory.toLowerCase().trim()

    if (newGroupId.length === 0) {
      NotificationManager.create({ type: 'error', timeOut: 20000, message: 'The category name is required. Could you enter a value for the category name?' })
      return
    }

    const duplicateCategory = groups.find((group) => (group.id === newGroupId))

    if (duplicateCategory) {
      NotificationManager.create({ type: 'error', timeOut: 20000, message: 'Category names must be unique. Could you enter another name?' })
      return
    }

    const copiedGroups = JSON.parse(JSON.stringify(groups))
    copiedGroups.push({
      id: newGroupId,
      label: newCategory,
      pages: []
    })

    setNewCategory('')
    updateGroups(copiedGroups)
  }

  function updateGroups (groups) {
    setGroups(groups)

    const allGroups = groups
    if (uncategorizedGroup && !allGroups.includes(uncategorizedGroup)) {
      allGroups.push(uncategorizedGroup)
    }
    reportTocContext.setNewGroups(allGroups)
  }

  useEffect(() => {
    if (report && filteredToc) {
      setGroups(filteredToc || report.groups)
    }
  }, [report, filteredToc])

  if (!Array.isArray(groups) || groups.length === 0) {
    return
  }

  pageNumber.current = 0

  // This key decides whether or not to animate category changes.
  const flipKey = groups.map((group) => (group.id)).join('')

  const filteredGroups = groups.filter((group) => !!group.id)

  return (<div className={`${classes.tocContainer} ${className || ''}`} style={style} data-qa="toc-list-container">
    {isEditable
      ? (
        <>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Flipper flipKey={flipKey}>
              {filteredGroups.map((group, index) => {
                return (
                  <Flipped key={group.id} flipId={encodeURI(group.id)}>
                    <div>
                      <CategoryBar groups={filteredGroups} index={index} isEditMode={true} onChange={updateGroups} />
                      <CategoryPages group={group} report={report} isEditMode={true} />
                    </div>
                  </Flipped>
                )
              })}
            </Flipper>
          </DragDropContext>
          <div className={classes.newCategoryContainer}>
            <form className={classes.newCategory} onSubmit={handleCategoryCreate}>
              <div className={classes.newCategoryLabel}>Category Name:</div>
              <TextInput
                value={newCategory}
                onChange={(event) => { setNewCategory(event.target.value) }}
                className={classes.newCategoryField}
                data-qa="toc-list-new-category-name" />
              <Button className={classes.newCategoryButton} variant="secondary" size="large" data-qa="toc-list-create-category-button">Create</Button>
            </form>
          </div>
        </>
      )
      : filteredGroups.map((group, index) => (
        <div key={group.id} data-qa={`toc-group-${group.id.replace(/"/g, '&quot;')}`}>
          <CategoryBar groups={filteredGroups} index={index} />
          <CategoryPages group={group} report={report} pageNumber={pageNumber} />
        </div>
      ))
    }
  </div>)
}
